import React from 'react'
import { connect } from 'react-redux'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import InboxLayout from '@groovehq/internal-design-system/lib/assets/icons/InboxLayout'
import { layoutNew as InboxLayoutV2 } from 'assets/icons/groove/v2'
import { Popup, Card, Image } from 'util/ui'
import { Label } from 'shared/components/ui/Typography'
import { Header, SubHeader, Text } from 'shared/ui'
import themeVars from 'ui_theme/site/globals/site.variables'

import { doSetFlag, selectIsFetchingFlags, selectFlag } from 'ducks/flags'
import {
  doUpdateUserPreferences,
  selectPrefersClassicView,
} from 'ducks/currentUser'

import classicPreview from 'assets/layout-classic.png'
import modernPreview from 'assets/layout-modern.png'

import GlobalNavItem from './GlobalNavItem'

const StyledPopup = styled(Popup)`
  box-shadow: ${themeVars.floatingShadowHover} !important;
  padding: ${props => props.theme.spacing['12']} !important;
  max-width: 728px !important;
  text-align: center;
`

const StyledCardGroup = styled(Card.Group)`
  margin-left: -${themeVars.relative8px} !important;
  margin-right: -${themeVars.relative8px} !important;
`

const StyledCard = styled(Card)`
  position: relative;
  padding: 0 !important;
  width: calc(50% - ${themeVars.relative16px}) !important;
  margin-left: ${themeVars.relative8px} !important;
  margin-right: ${themeVars.relative8px} !important;
  &:not(.raised):hover {
    border-color: ${themeVars.solidBorderColor} !important;
  }
`

const StyleImageContainer = css`
  padding-bottom: 24px !important;
`

const StyleDescription = css`
  padding-top: 0px !important;
`

const PreviewImage = styled(Image)`
  height: 128px;
`

const flagName = 'has_seen_inbox_layout_switcher'

class InboxLayoutSwitcher extends React.PureComponent {
  // eslint-disable-next-line react/sort-comp
  state = {
    isOpen: false,
  }

  handleOpen = () => {
    this.setState({ isOpen: true })
  }

  handleClose = () => {
    this.setState({ isOpen: false })
  }

  handleClickClassicView = () => {
    const { prefersClassicView, switchToClassicView } = this.props
    if (!prefersClassicView) {
      switchToClassicView()
    }
    this.setState({ isOpen: false })
  }

  handleClickModernView = () => {
    const { prefersClassicView, switchToModernView } = this.props
    if (prefersClassicView) {
      switchToModernView()
    }
    this.setState({ isOpen: false })
  }

  render() {
    const {
      switchToClassicView,
      switchToModernView,
      prefersClassicView,
      flagHasSeen,
      showTopNav,
      ...rest
    } = this.props
    const { isOpen } = this.state
    return (
      <StyledPopup
        basic
        flowing
        mountNode={app.suiPortalRoot}
        on="click"
        position="top left"
        horizontalOffset={-40}
        verticalOffset={-5}
        trigger={
          <GlobalNavItem
            {...rest}
            title="Layout"
            icon={showTopNav ? <InboxLayoutV2 /> : <InboxLayout />}
          />
        }
        open={isOpen}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
      >
        <Header
          as="h1"
          size="medium"
          css={css`
            &&& {
              margin-bottom: 0 !important;
              font-family: ${themeVars.walsheim} !important;
            }
          `}
        >
          Choose your layout
        </Header>
        <SubHeader
          as="h2"
          size="tiny"
          css={css`
            margin-top: 0 !important;
            margin-bottom: ${themeVars.relative24px} !important;
          `}
        >
          Choose a layout that best suits your day-to-day needs. Change at any
          time.
        </SubHeader>
        <StyledCardGroup stackable centered itemsPerRow={2}>
          <StyledCard
            link
            raised={prefersClassicView}
            as="div"
            id="inbox-layout-3column"
            onClick={this.handleClickClassicView}
            className="nodivider"
          >
            <Card.Content textAlign="center" css={StyleImageContainer}>
              <PreviewImage src={classicPreview} />
            </Card.Content>
            <Card.Header as={Label} size="large">
              3 column view
            </Card.Header>
            <Card.Content textAlign="center" css={StyleDescription}>
              <Text as="p" size="small">
                A simple 3 column layout where you navigate between the{' '}
                {app.t('ticket')}
                list and a selected {app.t('ticket')}.
              </Text>
            </Card.Content>
          </StyledCard>
          <StyledCard
            link
            raised={!prefersClassicView}
            as="div"
            id="inbox-layout-4column"
            onClick={this.handleClickModernView}
            className="nodivider"
          >
            <Card.Content textAlign="center" css={StyleImageContainer}>
              <PreviewImage src={modernPreview} />
            </Card.Content>
            <Card.Header as={Label} size="large">
              4 column view
            </Card.Header>
            <Card.Content textAlign="center" css={StyleDescription}>
              <Text as="p" size="small">
                4 Column layout, showing you both your {app.t('ticket')} list
                and a selected{` `}
                {app.t('ticket')} open at the same time.
              </Text>
            </Card.Content>
          </StyledCard>
        </StyledCardGroup>
      </StyledPopup>
    )
  }
}

const select = state => {
  return {
    prefersClassicView: selectPrefersClassicView(state),
    isFetchingFlags: selectIsFetchingFlags(state),
    hasSeen: selectFlag(state)(flagName),
  }
}

const perform = dispatch => {
  return {
    switchToClassicView: () =>
      dispatch(doUpdateUserPreferences({ classic_view: true })),
    switchToModernView: () =>
      dispatch(doUpdateUserPreferences({ classic_view: false })),
    flagHasSeen: () => dispatch(doSetFlag(flagName)),
  }
}

export default connect(select, perform)(InboxLayoutSwitcher)
